import {
  formatOklch,
  hexToOklch,
  hexToRgb,
  normalizeHex,
  normalizeHue,
  rgbToHex,
} from '@/lib/color-formats';
import type { GradientInterpolation, HarmonyId, PaletteSize } from '@/types/studio';
import { fitOklch } from '@/lib/color-input';

export {
  hexToOklch,
  hexToRgb,
  isValidHex,
  normalizeHex,
  normalizeHue,
  rgbToHex,
} from '@/lib/color-formats';

export const HARMONY_OPTIONS: ReadonlyArray<{
  id: HarmonyId;
  label: string;
  shortDescription: string;
}> = [
  {
    id: 'analogous',
    label: 'Analogous',
    shortDescription: 'Neighboring hues with a gentle visual rhythm.',
  },
  {
    id: 'complementary',
    label: 'Complementary',
    shortDescription: 'Two opposing hue families with strong separation.',
  },
  {
    id: 'split-complementary',
    label: 'Split complement',
    shortDescription: 'A base hue plus both neighbors of its opposite.',
  },
  {
    id: 'triadic',
    label: 'Triadic',
    shortDescription: 'Three evenly spaced hue families with broad range.',
  },
  {
    id: 'monochromatic',
    label: 'Monochromatic',
    shortDescription: 'One hue expressed through lightness and chroma.',
  },
  {
    id: 'tetradic',
    label: 'Tetradic',
    shortDescription: 'Two complementary pairs, balanced around the wheel.',
  },
  { id: 'square', label: 'Square', shortDescription: 'Four hue anchors at equal intervals.' },
];

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

export const formatHexAsOklch = (hex: string): string => {
  const color = hexToOklch(hex);
  return formatOklch(color.l, color.c, color.h);
};

const HARMONY_OFFSETS: Record<HarmonyId, Partial<Record<PaletteSize, number[]>>> = {
  analogous: {
    5: [-54, -27, 0, 27, 54],
    6: [-60, -36, -12, 12, 36, 60],
  },
  complementary: {
    5: [-18, 0, 18, 162, 198],
    6: [-24, 0, 24, 156, 180, 204],
  },
  'split-complementary': {
    5: [0, 18, 138, 162, 222],
    6: [-15, 15, 138, 162, 198, 222],
  },
  triadic: {
    5: [0, 24, 120, 216, 240],
    6: [0, 24, 120, 144, 240, 264],
  },
  monochromatic: {
    5: [0, 0, 0, 0, 0],
    6: [0, 0, 0, 0, 0, 0],
  },
  tetradic: {},
  square: {},
};

const LIGHTNESS_OFFSETS: Partial<Record<PaletteSize, number[]>> = {
  5: [0.16, 0.08, 0, -0.09, -0.18],
  6: [0.18, 0.11, 0.04, -0.04, -0.12, -0.2],
};

const CHROMA_FACTORS: Partial<Record<PaletteSize, number[]>> = {
  5: [0.7, 0.88, 1, 0.92, 0.72],
  6: [0.66, 0.8, 0.96, 1, 0.86, 0.68],
};

export const getHarmonyHueOffsets = (harmony: HarmonyId, count: PaletteSize): number[] => {
  const exact = HARMONY_OFFSETS[harmony][count];
  if (exact) return [...exact];
  const anchors: Record<HarmonyId, number[]> = {
    analogous: [0, -30, 30, -60, 60],
    complementary: [0, 180],
    'split-complementary': [0, 150, 210],
    triadic: [0, 120, 240],
    monochromatic: [0],
    tetradic: [0, 60, 180, 240],
    square: [0, 90, 180, 270],
  };
  return Array.from(
    { length: count },
    (_, index) => anchors[harmony][index % anchors[harmony].length]!,
  );
};

export const generateHarmonyPalette = ({
  seed,
  harmony,
  count,
  chroma,
  lightness,
}: {
  seed: string;
  harmony: HarmonyId;
  count: PaletteSize;
  chroma: number;
  lightness: number;
}): string[] => {
  const seedColor = hexToOklch(seed);
  const offsets = getHarmonyHueOffsets(harmony, count);
  const lightnessOffsets =
    LIGHTNESS_OFFSETS[count] ??
    Array.from({ length: count }, (_, i) => 0.2 - (0.4 * i) / (count - 1));
  const chromaFactors =
    CHROMA_FACTORS[count] ??
    Array.from({ length: count }, (_, i) => 0.65 + 0.35 * Math.sin((Math.PI * i) / (count - 1)));

  let usedSeedSlot = false;
  return offsets.map((offset, index) => {
    if (offset === 0 && !usedSeedSlot) {
      usedSeedSlot = true;
      return normalizeHex(seed);
    }
    const resolvedLightness = clamp(lightness + (lightnessOffsets[index] ?? 0), 0.28, 0.92);
    const resolvedChroma = clamp(chroma * (chromaFactors[index] ?? 1), 0.025, 0.29);
    return fitOklch(resolvedLightness, resolvedChroma, normalizeHue(seedColor.h + offset)).hex;
  });
};

export const mixHexColors = (
  start: string,
  end: string,
  amount: number,
  space: GradientInterpolation = 'srgb',
): string => {
  const ratio = clamp(amount, 0, 100) / 100;
  if (ratio === 0) return normalizeHex(start);
  if (ratio === 1) return normalizeHex(end);
  if (space !== 'srgb') {
    const a = hexToOklch(start);
    const b = hexToOklch(end);
    const lerp = (x: number, y: number) => x + (y - x) * ratio;
    if (space === 'oklch') {
      const h1 = a.c < 0.00001 ? b.h : a.h;
      const h2 = b.c < 0.00001 ? h1 : b.h;
      const delta = ((h2 - h1 + 540) % 360) - 180;
      return fitOklch(lerp(a.l, b.l), lerp(a.c, b.c), h1 + delta * ratio).hex;
    }
    const axisA = lerp(
      a.c * Math.cos((a.h * Math.PI) / 180),
      b.c * Math.cos((b.h * Math.PI) / 180),
    );
    const axisB = lerp(
      a.c * Math.sin((a.h * Math.PI) / 180),
      b.c * Math.sin((b.h * Math.PI) / 180),
    );
    return fitOklch(
      lerp(a.l, b.l),
      Math.hypot(axisA, axisB),
      (Math.atan2(axisB, axisA) * 180) / Math.PI,
    ).hex;
  }
  const startRgb = hexToRgb(start);
  const endRgb = hexToRgb(end);
  return rgbToHex(
    startRgb.r + (endRgb.r - startRgb.r) * ratio,
    startRgb.g + (endRgb.g - startRgb.g) * ratio,
    startRgb.b + (endRgb.b - startRgb.b) * ratio,
  );
};

export const generatePaletteCss = (colors: string[]): string =>
  [
    ':root {',
    ...colors.map((color, index) => `  --color-palette-${index + 1}: ${normalizeHex(color)};`),
    '}',
  ].join('\n');

export const generatePaletteJson = (colors: string[]): string =>
  JSON.stringify(
    Object.fromEntries(colors.map((color, index) => [`color-${index + 1}`, normalizeHex(color)])),
    null,
    2,
  );
